import MapboxDraw from '@mapbox/mapbox-gl-draw'
import { useControl } from 'react-map-gl'
import { useMemo } from 'react'
import type { MapRef, ControlPosition } from 'react-map-gl'

const noop = () => {}

type DrawControlProps = ConstructorParameters<typeof MapboxDraw>[0] & {
  position?: ControlPosition
  initGeometry?: GeoJSON.FeatureCollection
  initFeatures: (features: { [key: string]: GeoJSON.Feature }) => void
  onCreate?: (evt: { features: object[] }) => void
  onUpdate?: (evt: { features: object[]; action: string }) => void
  onDelete?: (evt: { features: object[] }) => void
}

export default function DrawControl(props: DrawControlProps) {
  const { initGeometry, initFeatures, onCreate, onUpdate, onDelete, position, ...drawOptions } = props
  const handleCreate = onCreate ?? noop
  const handleUpdate = onUpdate ?? noop
  const handleDelete = onDelete ?? noop
  const draw = useMemo(() => new MapboxDraw(drawOptions), [])
  useControl<MapboxDraw>(
    () => draw,
    ({ map }: { map: MapRef }) => {
      if (initGeometry) {
        // Adding features one-by-one triggers a render per feature in mapbox-gl-draw.
        // Adding the full FeatureCollection renders once and is significantly faster for multipolygons.
        draw.add(initGeometry)
        const nextFeatures: { [key: string]: GeoJSON.Feature } = {}
        for (const feature of draw.getAll()?.features ?? []) {
          const featureId = (feature as any)?.id
          if (!featureId) continue
          nextFeatures[String(featureId)] = feature as GeoJSON.Feature
        }
        initFeatures(nextFeatures)
      }
      map.on('draw.create', handleCreate)
      map.on('draw.update', handleUpdate)
      map.on('draw.delete', handleDelete)
    },
    ({ map }: { map: MapRef }) => {
      map.off('draw.create', handleCreate)
      map.off('draw.update', handleUpdate)
      map.off('draw.delete', handleDelete)
    },
    {
      position,
    },
  )

  return null
}

DrawControl.defaultProps = {
  onCreate: () => {},
  onUpdate: () => {},
  onDelete: () => {},
  initGeometry: undefined,
  initFeatures: () => {},
}
